import { TocItemsModule } from "@viewer/toc-items/toc-items.module";
import { NgModule } from "@angular/core";
import { CommonModule } from "@angular/common";

import { NoteModuleRoutingModule } from "@viewer/toc-items/note-module/note-routing.module";
import { NoteListComponent } from "@viewer/toc-items/note-module/note-list/note-list.component";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatIconModule } from "@angular/material/icon";
import { MatInputModule } from "@angular/material/input";
import { MAT_SELECT_CONFIG, MatSelectModule } from "@angular/material/select";
import { MatCardModule } from "@angular/material/card";
import { MatPaginatorModule } from "@angular/material/paginator";
import { MatTableModule } from "@angular/material/table";
import { MatToolbarModule } from "@angular/material/toolbar";
import { FormsModule, ReactiveFormsModule } from "@angular/forms";
import { NoteComponent } from "@viewer/toc-items/note-module/note/note.component";
import { NoteAnchorDirective } from "@viewer/toc-items/note-module/note/note-anchor.directive";
import { TocItemCanDeactivateGuard } from "@viewer/toc-items/guards/tocItem-can-deactivate-guard.service";
import { SharedModule } from "@viewer/shared-module/shared.module";
import { MatMenuModule } from "@angular/material/menu";
import { MatButtonModule } from "@angular/material/button";
import { WebTekDialogComponent } from "@viewer/toc-items/note-module/webtek-dialog/web-tek-dialog.component";
import { MatProgressSpinnerModule } from "@angular/material/progress-spinner";
import { MatDialogModule } from "@angular/material/dialog";
import { WebTekService } from "@viewer/toc-items/note-module/web-tek.service";
import { WebTekIdsService } from "@viewer/toc-items/note-module/web-tek-ids.service";
import { ParseRevisionPipeModule } from "@orion2/utils/ParseRevisionPipeModule";
import { ByteFormatPipe, MaterialFileInputModule } from "ngx-custom-material-file-input";
import { MatCheckboxModule } from "@angular/material/checkbox";

@NgModule({
  imports: [
    CommonModule,
    NoteModuleRoutingModule,
    CommonModule,
    MatTableModule,
    MatPaginatorModule,
    MatIconModule,
    FormsModule,
    SharedModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    ReactiveFormsModule,
    MatCardModule,
    MatMenuModule,
    MatButtonModule,
    MatProgressSpinnerModule,
    MatDialogModule,
    MatToolbarModule,
    ParseRevisionPipeModule,
    TocItemsModule,
    MaterialFileInputModule,
    MatCheckboxModule
  ],
  providers: [
    TocItemCanDeactivateGuard,
    WebTekService,
    WebTekIdsService,
    ByteFormatPipe,
    { provide: MAT_SELECT_CONFIG, useValue: { hideSingleSelectionIndicator: true } }
  ],
  declarations: [NoteListComponent, NoteComponent, WebTekDialogComponent, NoteAnchorDirective],
  exports: [NoteListComponent, NoteComponent, NoteAnchorDirective]
})
export class NoteModule {}

/**
 * Response body of the GET '/me' of WebTek API
 */
export interface MeResponse {
  id: number;
  email: string;
  isStaffMember: boolean;
  firstName: string;
  lastName: string;
  customerOperator: boolean;
}

/**
 * Request body of the POST '/technicalEvent' of WebTek API
 */
export interface TechnicalEventBody {
  visibility: "EXTERNAL" | "INTERNAL";
  customerPriorityId: number;
  requestedFinalAnswerDate: string; // ex: '2018-11-15T14:15:11.107Z'
  targetDate?: string;
  subject: string;
  aircrafts?: {
    typeId: number;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    serialNumber?: any;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    version?: any;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    registration?: any;
    totalTimeSinceNew?: number;
    landings?: number;
  }[];
  owner?: {
    id: number;
    firstName: string;
    lastName: string;
  };
  contact?: {
    id?: number;
    isOperator?: boolean;
    centers?: {
      maintenance: {};
      operator: {};
    };
  };
  subTechnicalEvents?: {
    businessDomainId: number;
    ataId: number;
    visibility: "CUSTOMER" | "AH_NETWORK" | "AH_AHD";
    orionVersion: string;
    eventTypeId: number;
    subEventTypeId?: number;
    businessDomainMetadata: {
      techData: {
        workCardRef: string;
      };
    };
    owner?: {
      id: number;
      firstName: string;
      lastName: string;
    };
  }[];
}

/**
 * Response body of the POST '/technicalEventResult' of WebTek API
 */
export interface TechnicalEventResult {
  id?: number;
  reference?: string;
  subTechnicalEvents?: number[];
}

export interface FromTo {
  id: number;
  name: string;
  address: string;
  isStaffMember: boolean;
}

/**
 * Request body of the POST '/messaging/message' of WebTek API
 */
export interface MessagesBody {
  from?: FromTo;
  to?: FromTo;
  attachments?: Object[];
  technicalEventId: number;
  subTechnicalEventId: number;
  channel: "AH_AHD" | "AH_NETWORK" | "CUSTOMER";
  subject: string;
  text?: string;
  actionType: "PROVIDE_INFO" | "REQUEST_MORE_INFO" | "FINAL_ANSWER";
  draft: boolean;
}
